<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useAuthStore } from '../../stores/auth'
import { generateDefaultDeviceLabel } from '../../services/account/deviceLabel'
import { secureStorage } from '../../services/crypto/secureStorage'
import LocalDeviceActions from './LocalDeviceActions.vue'
import ConfirmDialog from '../app/ConfirmDialog.vue'
import PasswordStrengthReport from './PasswordStrengthReport.vue'
import { getPasswordPolicyError } from '../../services/crypto/passwordStrength'
import { MARK_PATH, MARK_VIEWBOX } from '../../constants/brandMark'
import { mountRings } from '../../utils/echoRings'
import { OtherAccountWritingError } from '../../services/account/otherAccountWriting'

withDefaults(
  defineProps<{
    restoringSession?: boolean
  }>(),
  {
    restoringSession: false,
  }
)

const auth = useAuthStore()
const username = ref(auth.username || '')
const password = ref('')
const showPassword = ref(false)
const localError = ref('')
const passwordInput = ref<HTMLInputElement | null>(null)
const mode = ref<'login' | 'recover' | 'username'>('login')
const recoveryUsername = ref(auth.username || '')
const recoveryKey = ref('')
const newPassword = ref('')
const newPasswordScore = ref<number | null>(null)
const recoveryError = ref('')
const reminderEmail = ref('')
const reminderMessage = ref('')
const reminderLoading = ref(false)
/** Set when sign-in is blocked by another account's writing; the local data section below is where it is resolved. */
const otherAccount = ref<OtherAccountWritingError | null>(null)
const localDataNeeded = ref(false)
const localDataSection = ref<HTMLDetailsElement | null>(null)
const localDataTitle = computed(() => auth.username
  ? `Writing from ${auth.username} on this device`
  : 'Writing from another account on this device')

const canSubmit = computed(() => username.value.trim().length > 0 && password.value.length > 0 && !auth.loading)
const windowTitle = computed(() => {
  if (mode.value === 'recover') return 'Account recovery'
  if (mode.value === 'username') return 'Username reminder'
  return 'Sign in'
})

const room = ref<HTMLElement | null>(null)
const ringCanvas = ref<HTMLCanvasElement | null>(null)
const ringMark = ref<SVGSVGElement | null>(null)
const signInWindow = ref<HTMLElement | null>(null)
let disposeRings: (() => void) | null = null

onMounted(() => {
  // The website's echo lines: every ring an exact offset of the mark and of the sign-in window.
  if (room.value && ringCanvas.value && ringMark.value) {
    disposeRings = mountRings({
      host: room.value,
      canvas: ringCanvas.value,
      mark: ringMark.value,
      box: signInWindow.value,
      boxGap: 14,
      step: 4.5,
      pulse: true,
      maxPixels: 1.2e6,
    })
  }
  nextTick(() => {
    const target = username.value ? passwordInput.value : document.querySelector<HTMLInputElement>('#login-username')
    target?.focus()
  })
})

onBeforeUnmount(() => disposeRings?.())

/** Opens the export/removal options when the error points to them. */
async function noteSignInError(error: unknown) {
  otherAccount.value = error instanceof OtherAccountWritingError ? error : null
  if (!otherAccount.value) return
  localDataNeeded.value = true
  await nextTick()
  if (!localDataSection.value) return
  localDataSection.value.open = true
  localDataSection.value.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
}

async function handleSubmit() {
  if (!canSubmit.value) return
  localError.value = ''
  otherAccount.value = null

  try {
    const savedLabel = await secureStorage.getDeviceLabel()
    const deviceLabel = savedLabel || await generateDefaultDeviceLabel()
    await auth.signIn(username.value.trim(), password.value, deviceLabel)
    password.value = ''
  } catch (error) {
    localError.value = error instanceof Error ? error.message : 'We could not sign you in. Please try again.'
    password.value = ''
    await nextTick()
    passwordInput.value?.focus()
    await noteSignInError(error)
  }
}

function setMode(nextMode: 'login' | 'recover' | 'username') {
  mode.value = nextMode
  localError.value = ''
  recoveryError.value = ''
  reminderMessage.value = ''
  otherAccount.value = null
}

async function handleRecovery() {
  recoveryError.value = ''
  otherAccount.value = null
  const policyError = getPasswordPolicyError(newPassword.value, newPasswordScore.value)
  if (!recoveryUsername.value.trim() || !recoveryKey.value.trim() || !newPassword.value) {
    recoveryError.value = 'Enter your username, recovery key, and a new password.'
    return
  }
  if (!/^[\x20-\x7E]+$/.test(newPassword.value)) {
    recoveryError.value = 'Password may only contain printable ASCII characters.'
    return
  }
  if (policyError) {
    recoveryError.value = policyError
    return
  }

  try {
    await auth.resetPasswordWithRecoveryKey(
      recoveryUsername.value.trim(),
      recoveryKey.value.trim(),
      newPassword.value,
    )
  } catch (error) {
    recoveryError.value = error instanceof Error ? error.message : 'Recovery failed. Check your username and recovery key.'
    await noteSignInError(error)
  }
}

async function handleUsernameReminder() {
  if (!reminderEmail.value.trim()) return
  reminderLoading.value = true
  reminderMessage.value = ''
  try {
    await auth.requestUsernameReminder(reminderEmail.value.trim())
    reminderMessage.value = "If that email matches an account, we've sent the username to it."
    reminderEmail.value = ''
  } catch (error) {
    reminderMessage.value = error instanceof Error ? error.message : 'Something went wrong. Please try again shortly.'
  } finally {
    reminderLoading.value = false
  }
}
</script>

<template>
  <main class="login-screen">
    <div ref="room" class="login-room">
      <canvas ref="ringCanvas" class="login-rings" aria-hidden="true"></canvas>
      <svg ref="ringMark" class="login-mark" :viewBox="MARK_VIEWBOX" aria-hidden="true">
        <path fill-rule="evenodd" :d="MARK_PATH" />
      </svg>

      <div class="login-stage">
        <section ref="signInWindow" class="login-window" aria-labelledby="login-heading">
          <div class="login-window-bar">
            <span>{{ windowTitle }}</span>
            <span class="login-lock">Encrypted</span>
          </div>

          <div class="login-window-body">
            <span class="login-logo" role="img" aria-label="The Superelectric Typewriter">
              <svg class="login-logo-mark" :viewBox="MARK_VIEWBOX" aria-hidden="true">
                <path fill-rule="evenodd" :d="MARK_PATH" />
              </svg>
              <span class="login-logo-lines" aria-hidden="true">
                <span>The</span><span>Superelectric</span><span>Typewriter<span class="login-dot">.</span></span>
              </span>
            </span>

            <div v-if="restoringSession" class="login-restoring" role="status" aria-live="polite">
              <p>Opening your writing room<span class="login-caret" aria-hidden="true"></span></p>
            </div>

            <template v-else>
              <template v-if="mode === 'login'">
                <header class="login-header">
                  <h1 id="login-heading">Welcome back<span class="login-dot">.</span></h1>
                  <p>Sign in to open your writing space.</p>
                </header>

                <form class="login-form" @submit.prevent="handleSubmit">
                  <label class="login-field">
                    <span>Username</span>
                    <input
                      id="login-username"
                      v-model="username"
                      name="username"
                      type="text"
                      autocomplete="username"
                      autocapitalize="none"
                      spellcheck="false"
                      enterkeyhint="next"
                      :disabled="auth.loading"
                      required
                    >
                  </label>

                  <label class="login-field">
                    <span>Password</span>
                    <span class="login-password-wrap">
                      <input
                        ref="passwordInput"
                        v-model="password"
                        name="password"
                        :type="showPassword ? 'text' : 'password'"
                        autocomplete="current-password"
                        enterkeyhint="go"
                        :disabled="auth.loading"
                        required
                      >
                      <button
                        type="button"
                        class="login-password-toggle"
                        :aria-label="showPassword ? 'Hide password' : 'Show password'"
                        :aria-pressed="showPassword"
                        @click="showPassword = !showPassword"
                      >{{ showPassword ? 'Hide' : 'Show' }}</button>
                    </span>
                  </label>

                  <p v-if="otherAccount" class="login-error" role="alert"><strong>{{ otherAccount.lead }}</strong> {{ otherAccount.detail }}</p>
                  <p v-else-if="localError || auth.error" class="login-error" role="alert">{{ localError || auth.error }}</p>

                  <button class="login-submit" type="submit" :disabled="!canSubmit">
                    <span v-if="auth.loading" class="login-button-loader" aria-hidden="true"></span>
                    <span>{{ auth.loading ? 'Opening your space…' : 'Open my writing space' }}</span>
                  </button>
                </form>

                <div class="login-help-actions">
                  <button type="button" @click="setMode('username')">Forgot username?</button>
                  <button type="button" @click="setMode('recover')">Forgot password?</button>
                </div>
              </template>

              <template v-else-if="mode === 'recover'">
                <button type="button" class="login-back" @click="setMode('login')">← Back to sign in</button>
                <header class="login-header">
                  <h1 id="login-heading">Set a new password<span class="login-dot">.</span></h1>
                  <p>Your recovery key keeps this process private, even from us.</p>
                </header>
                <form class="login-form" @submit.prevent="handleRecovery">
                  <label class="login-field">
                    <span>Username</span>
                    <input v-model="recoveryUsername" type="text" autocomplete="username" autocapitalize="none" required>
                  </label>
                  <label class="login-field">
                    <span>Recovery key</span>
                    <input v-model="recoveryKey" class="login-key-input" type="text" autocomplete="off" autocapitalize="none" spellcheck="false" placeholder="Paste your recovery key" required>
                  </label>
                  <label class="login-field">
                    <span>New password</span>
                    <input v-model="newPassword" type="password" autocomplete="new-password" required>
                  </label>
                  <div class="login-password-report">
                    <PasswordStrengthReport
                      :password="newPassword"
                      :username="recoveryUsername"
                      email=""
                      @update:score="newPasswordScore = $event"
                    />
                  </div>
                  <p v-if="otherAccount" class="login-error" role="alert"><strong>{{ otherAccount.lead }}</strong> {{ otherAccount.detail }}</p>
                  <p v-else-if="recoveryError || auth.error" class="login-error" role="alert">{{ recoveryError || auth.error }}</p>
                  <button class="login-submit" type="submit" :disabled="auth.loading">
                    <span v-if="auth.loading" class="login-button-loader" aria-hidden="true"></span>
                    <span>{{ auth.loading ? 'Securing your account…' : 'Reset password & sign in' }}</span>
                  </button>
                </form>
              </template>

              <template v-else>
                <button type="button" class="login-back" @click="setMode('login')">← Back to sign in</button>
                <header class="login-header">
                  <h1 id="login-heading">Let’s find your account<span class="login-dot">.</span></h1>
                  <p>Enter the email address associated with your purchase.</p>
                </header>
                <form class="login-form" @submit.prevent="handleUsernameReminder">
                  <label class="login-field">
                    <span>Email address</span>
                    <input v-model="reminderEmail" type="email" autocomplete="email" inputmode="email" placeholder="you@example.com" required>
                  </label>
                  <p v-if="reminderMessage" class="login-reminder-message" role="status">{{ reminderMessage }}</p>
                  <button class="login-submit" type="submit" :disabled="reminderLoading || !reminderEmail.trim()">
                    <span v-if="reminderLoading" class="login-button-loader" aria-hidden="true"></span>
                    <span>{{ reminderLoading ? 'Sending…' : 'Send username reminder' }}</span>
                  </button>
                </form>
              </template>
            </template>

            <details v-if="auth.username || localDataNeeded" ref="localDataSection" class="login-local-removal settings-modal">
              <summary>{{ localDataTitle }}</summary>
              <LocalDeviceActions />
            </details>
            <footer class="login-footer">Your work is end-to-end encrypted. Made for the long draft.</footer>
          </div>
        </section>
      </div>
    </div>
  </main>
  <ConfirmDialog />
</template>

<style scoped>
.login-local-removal { margin-top: 20px; padding: 12px; border-radius: 8px; background: var(--surface-bg); color: var(--text-normal); font-family: var(--ui-text); }
.login-local-removal summary { cursor: pointer; font-size: 13px; }
/* The website's sign-in room (superelectric-site RingRoom): a teal field, the
   mark cropped off the left edge, and echo lines wrapping around the mark and
   the sign-in window together. Colors are the logo's, fixed, not the theme's:
   this is the brand's front door, seen before any settings exist. */
.login-screen {
  --teal: #1f7a72;
  --teal-d: #155b55;
  --deep: #123f3a;
  --paper: #f0e3c4;
  --paper-l: #f8f0dc;
  --paper-d: #e9dcbb;
  --coral: #ff6b45;
  --coral-l: #ffab8a;
  --coral-d: #e65832;
  --line: rgba(18, 63, 58, .2);
  --mono: var(--ui-mono);
  --serif: 'Besley', Georgia, serif;
  position: fixed;
  inset: 0;
  overflow-y: auto;
  color: var(--deep);
  background: var(--teal);
  -webkit-font-smoothing: antialiased;
}

.login-room {
  --rm: min(62vw, 760px);
  position: relative;
  min-height: 100%;
  display: grid;
  align-items: center;
  padding: max(48px, var(--safe-top)) clamp(24px, 6vw, 96px) max(48px, var(--safe-bottom));
  box-sizing: border-box;
  overflow: hidden;
}

.login-rings {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
}

.login-mark {
  position: absolute;
  top: 50%;
  left: calc(-1 * min(22vw, 280px));
  width: var(--rm);
  height: var(--rm);
  transform: translateY(-50%);
  fill: var(--coral);
}

.login-stage {
  position: relative;
  display: flex;
  justify-content: flex-end;
  width: min(1240px, 100%);
  margin-inline: auto;
}

/* ─── The window ─────────────────────────────────────────────────────────── */
.login-window {
  width: min(460px, 100%);
  overflow: hidden;
  background: var(--paper-l);
  border: 1.5px solid var(--deep);
  border-radius: 6px;
  box-shadow: 0 18px 36px -18px rgba(10, 39, 36, .45);
}

.login-window-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 14px;
  color: var(--paper);
  background: var(--deep);
  font: 500 12.5px/1.2 var(--mono);
}

.login-lock {
  padding: 2px 7px;
  border-radius: 3px;
  color: var(--deep);
  background: var(--coral-l);
  font-size: 10.5px;
}

.login-window-body { padding: clamp(24px, 4vw, 36px); }

/* The M1 stack lockup, cream scheme (designs/web/superelectric-logo.css). */
.login-logo {
  display: flex;
  width: fit-content;
  align-items: flex-start;
  gap: .714em;
  margin-bottom: 30px;
  font-size: 13px;
  font-family: var(--mono);
  color: var(--deep);
}
.login-logo-mark { flex: none; width: 3.93em; height: 3.93em; fill: var(--coral); }
.login-logo-lines {
  font-weight: 700;
  line-height: 1.02;
  letter-spacing: -.03em;
  border-top: .089em solid currentColor;
  padding-top: .286em;
}
.login-logo-lines > span { display: block; white-space: nowrap; }
.login-logo-lines > span:first-child { font-weight: 400; }
.login-dot { color: var(--coral-d); }

.login-header {
  margin-bottom: 26px;
  padding-top: 22px;
  border-top: 3px solid var(--deep);
}

.login-header h1 {
  margin: 0;
  font: 700 clamp(28px, 3vw, 36px)/1.05 var(--mono);
  letter-spacing: -.045em;
  text-wrap: balance;
}

.login-header p {
  margin: 12px 0 0;
  font: 400 16px/1.55 var(--serif);
}

/* ─── Form ───────────────────────────────────────────────────────────────── */
.login-form { display: grid; gap: 20px; }
.login-field { display: grid; gap: 7px; }

.login-field > span:first-child {
  font: 600 11px/1.3 var(--mono);
  letter-spacing: .12em;
  text-transform: uppercase;
}

.login-field input {
  width: 100%;
  height: 48px;
  box-sizing: border-box;
  padding: 0 14px;
  border: 0;
  border-bottom: 2px solid var(--deep);
  border-radius: 4px 4px 0 0;
  outline: none;
  color: var(--deep);
  background: var(--paper);
  font: 400 16px/1.3 var(--mono);
  transition: background .12s ease, border-color .12s ease;
}

.login-field input::placeholder { color: rgba(18, 63, 58, .42); }
.login-field input:hover { background: var(--paper-d); }
.login-field input:focus { background: #fffbef; border-bottom-color: var(--coral); }
.login-field input:disabled { opacity: .6; }
.login-key-input { letter-spacing: .04em; }

.login-password-wrap { position: relative; display: block; }
.login-password-wrap input { padding-right: 68px; }

.login-password-toggle {
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  padding: 6px 8px;
  border-radius: 3px;
  color: var(--teal-d);
  font: 600 11px/1 var(--mono);
  letter-spacing: .08em;
  text-transform: uppercase;
}
.login-password-toggle:hover { background: rgba(31, 122, 114, .1); }

.login-error strong { display: block; margin-bottom: 4px; font-weight: 600; }
.login-error,
.login-reminder-message {
  margin: 0;
  padding: 12px 14px 12px 16px;
  border-left: 3px solid var(--coral-d);
  border-radius: 0 4px 4px 0;
  background: rgba(255, 107, 69, .1);
  font: 400 14.5px/1.5 var(--serif);
}
.login-reminder-message { border-left-color: var(--teal); background: rgba(31, 122, 114, .1); }

/* A typewriter key: coral, with a pressed-in bottom edge that travels down. */
.login-submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  margin-top: 4px;
  padding: 15px 20px 17px;
  border-radius: 6px;
  color: var(--deep);
  background: var(--coral);
  box-shadow: inset 0 -3px 0 rgba(0, 0, 0, .2);
  font: 600 16px/1 var(--mono);
  letter-spacing: -.02em;
  transition: transform .06s, box-shadow .06s, filter .12s;
}
.login-submit:not(:disabled):hover { filter: brightness(1.06); }
.login-submit:not(:disabled):active { transform: translateY(2px); box-shadow: inset 0 -1px 0 rgba(0, 0, 0, .2); }
.login-submit:disabled { cursor: not-allowed; opacity: .55; }
.login-submit:focus-visible,
.login-help-actions button:focus-visible,
.login-back:focus-visible,
.login-password-toggle:focus-visible { outline: 2px solid var(--coral-d); outline-offset: 3px; }

.login-help-actions {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 18px;
}

.login-help-actions button,
.login-back {
  padding: 4px 0;
  color: var(--deep);
  font: 500 13px/1.3 var(--mono);
  letter-spacing: -.01em;
  text-decoration: underline;
  text-decoration-color: var(--coral-d);
  text-underline-offset: 4px;
}
.login-help-actions button:hover,
.login-back:hover { color: var(--teal-d); }

.login-back {
  display: block;
  margin-bottom: 20px;
  text-decoration: none;
}

.login-password-report {
  --text-normal: rgba(18, 63, 58, .85);
  --text-muted: rgba(18, 63, 58, .6);
  --white-rgb: 18, 63, 58;
  --accent: var(--teal);
  margin-top: -6px;
}

.login-footer {
  margin-top: 28px;
  padding-top: 16px;
  border-top: 1.5px solid var(--line);
  font: 400 12px/1.5 var(--mono);
  opacity: .75;
}

/* ─── Waiting ────────────────────────────────────────────────────────────── */
.login-restoring p {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  padding-top: 22px;
  border-top: 3px solid var(--deep);
  font: 600 16px/1.4 var(--mono);
}

/* The editor's caret, waiting. */
.login-caret {
  display: inline-block;
  width: 2px;
  height: 1.15em;
  margin-left: 3px;
  background: var(--coral-d);
  animation: login-blink 1.06s steps(1) infinite;
}

.login-button-loader {
  width: 14px;
  height: 14px;
  box-sizing: border-box;
  border: 2px solid rgba(18, 63, 58, .3);
  border-top-color: var(--deep);
  border-radius: 50%;
  animation: login-spin .7s linear infinite;
}

@keyframes login-blink { 50% { opacity: 0; } }
@keyframes login-spin { to { transform: rotate(360deg); } }

/* ─── Phones and narrow windows: the mark above, the window below ────────── */
@media (max-width: 820px) {
  .login-room {
    --rm: 300px;
    align-items: start;
    padding: calc(170px + var(--safe-top)) 16px max(32px, var(--safe-bottom));
  }
  .login-mark {
    top: calc(-70px + var(--safe-top));
    left: 50%;
    transform: translateX(-50%);
  }
  .login-stage { justify-content: center; }
}

@media (max-width: 380px) {
  .login-help-actions { flex-direction: column; align-items: flex-start; gap: 4px; }
}

@media (prefers-reduced-motion: reduce) {
  .login-caret,
  .login-button-loader { animation: none; }
  .login-submit,
  .login-field input { transition: none; }
}
</style>
